import { motion, useScroll, useTransform } from "framer-motion";
import { useRef } from "react";
import { ArrowDown, Phone } from "lucide-react";
import { maskLine, waLink, telLink } from "@/lib/site-utils";
import { IMAGES, CONTACT } from "@/data/site";

const HEADLINE = ["SS CONSTRUCTION", "& STUDENT", "TRAVELS"];

export const Hero = () => {
  const ref = useRef(null);
  const { scrollYProgress } = useScroll({ target: ref, offset: ["start start", "end start"] });
  const imgY = useTransform(scrollYProgress, [0, 1], ["0%", "25%"]);
  const imgScale = useTransform(scrollYProgress, [0, 1], [1, 1.15]);
  const textY = useTransform(scrollYProgress, [0, 1], ["0%", "-30%"]);

  return (
    <section ref={ref} id="home" className="relative min-h-[100svh] overflow-hidden" data-testid="hero">
      {/* Parallax background */}
      <motion.div style={{ y: imgY, scale: imgScale }} className="absolute inset-0 z-0">
        <img src={IMAGES.jcbHero} alt="JCB Excavator Ramganjmandi" className="h-full w-full object-cover" />
        <div className="absolute inset-0 bg-asphalt/55" />
        <div className="absolute inset-0 bg-gradient-to-t from-asphalt via-asphalt/40 to-asphalt/20" />
      </motion.div>

      <motion.div style={{ y: textY }} className="relative z-10 mx-auto max-w-[1600px] px-6 md:px-12 pt-40 md:pt-52 pb-20">
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ delay: 0.15 }}
          className="flex items-center gap-4 mb-8"
        >
          <span className="h-px w-12 bg-jcb" />
          <span className="font-plex text-xs md:text-sm tracking-[0.25em] uppercase text-jcb">
            {CONTACT.areaEn}
          </span>
        </motion.div>

        <h1 className="font-khand font-700 uppercase leading-[0.85] tracking-tighter text-[15vw] md:text-[9vw] text-[#f5f5f0]">
          {HEADLINE.map((line, i) => (
            <span key={i} className="reveal-mask">
              <motion.span
                custom={i}
                variants={maskLine}
                initial="hidden"
                animate="show"
                className={`block ${i === 2 ? "text-jcb" : ""}`}
              >
                {line}
              </motion.span>
            </span>
          ))}
        </h1>

        <motion.p
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 1, duration: 0.8 }}
          className="mt-8 max-w-xl font-hind text-neutral-300 text-base md:text-lg leading-relaxed"
        >
          JCB मशीनें व अनुभवी ऑपरेटर से लेकर Ertiga व Tigor टैक्सी तक — एक ही जगह से भरोसेमंद निर्माण और यात्रा सेवाएँ।
          <span className="block mt-1 text-neutral-400 text-sm">
            Heavy machinery &amp; reliable travels, trusted across Ramganjmandi–Kota.
          </span>
        </motion.p>

        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 1.2, duration: 0.8 }}
          className="mt-10 flex flex-wrap items-center gap-4"
        >
          <a
            data-testid="hero-whatsapp-btn"
            href={waLink("नमस्ते SS Group! मुझे बुकिंग की जानकारी चाहिए।")}
            target="_blank"
            rel="noreferrer"
            className="group flex items-center gap-3 bg-jcb text-black px-8 py-4 font-khand font-600 text-lg tracking-wide hover:bg-white transition-colors duration-200"
          >
            WhatsApp बुकिंग
            <ArrowDown className="rotate-[-135deg] group-hover:translate-x-1 group-hover:-translate-y-1 transition-transform" size={18} />
          </a>
          <a
            data-testid="hero-call-btn"
            href={telLink()}
            className="flex items-center gap-3 border border-white/25 text-white px-8 py-4 font-khand font-600 text-lg tracking-wide hover:border-jcb hover:text-jcb transition-colors duration-200"
          >
            <Phone size={18} /> Call Now
          </a>
        </motion.div>
      </motion.div>

      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        transition={{ delay: 1.6 }}
        className="absolute bottom-8 left-6 md:left-12 flex items-center gap-3 text-neutral-500"
      >
        <ArrowDown size={16} className="animate-bounce" />
        <span className="font-plex text-[10px] tracking-[0.25em] uppercase">Scroll to explore</span>
      </motion.div>
    </section>
  );
};
